import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { KeepInTouchTemplateRequest } from '../../../../../models/KeepInTouchAgent';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { AIPromptUsage } from '../../../../../models/AIPromptTemplate';
import { AIPromptField, useAIPromptField } from '../../../../../shared/components/ai/AIPromptField';
import {
	useCreateKeepInTouchTemplateMutation,
	useUpdateKeepInTouchTemplateMutation,
} from '../../../../../queries/KeepInTouchAgent';
import { Button } from '../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../components-v2/Dialog';
import { Input } from '../../../../components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../components-v2/Select';
import { AIPromptBoxFooter } from '../../../../components-v2/ai/AIPromptBox';
import { InputGroupText } from '../../../../components-v2/InputGroup';
import { Toggle } from '../../../../components/Toggle';
import { styleSheet } from './styles';

const MAX_AUDIENCE_LENGTH = 5000;

interface ConfigurationDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	editingConfig?: Api.KeepInTouchTemplate | null;
}

export const ConfigurationDialog: React.FC<ConfigurationDialogProps> = ({ open, onOpenChange, editingConfig }) => {
	const userSession = useUserSession();
	const isAdmin = userSession.userRole === Api.EUserRole.Admin || userSession.userRole === Api.EUserRole.SuperAdmin;
	const isEditing = !!editingConfig;

	const [name, setName] = React.useState('');
	const [audiencePrompt, setAudiencePrompt] = React.useState('');
	const [objectivePrompt, setObjectivePrompt] = React.useState('');
	const audienceField = useAIPromptField({
		onChange: value => setAudiencePrompt(value.slice(0, MAX_AUDIENCE_LENGTH)),
		value: audiencePrompt,
	});
	const researchField = useAIPromptField({ onChange: setObjectivePrompt, value: objectivePrompt });
	const [scope, setScope] = React.useState<KeepInTouchTemplateRequest['scope']>(Api.AIAgentTemplateScope.User);
	const [enabled, setEnabled] = React.useState(true);

	React.useEffect(() => {
		if (editingConfig) {
			setName(editingConfig.name);
			setAudiencePrompt(editingConfig.audiencePrompt);
			setObjectivePrompt(editingConfig.objectivePrompt ?? '');
			setScope(
				editingConfig.scope === Api.AIAgentTemplateScope.Account
					? Api.AIAgentTemplateScope.Account
					: Api.AIAgentTemplateScope.User
			);
			setEnabled(editingConfig.scheduleEnabled ?? false);
		} else {
			setName('');
			setAudiencePrompt('');
			setObjectivePrompt('');
			setScope(Api.AIAgentTemplateScope.User);
			setEnabled(true);
		}
	}, [editingConfig, open]);

	const createMutation = useCreateKeepInTouchTemplateMutation({
		onSuccess: () => {
			toast.success('Configuration created');
			onOpenChange(false);
		},
		onError: () => {
			toast.error('Failed to create configuration');
		},
	});

	const updateMutation = useUpdateKeepInTouchTemplateMutation({
		onSuccess: () => {
			toast.success('Configuration updated');
			onOpenChange(false);
		},
		onError: () => {
			toast.error('Failed to update configuration');
		},
	});

	const isPending = createMutation.isPending || updateMutation.isPending;
	const hasUnfilledPlaceholder = !!audienceField.unfilledPlaceholder || !!researchField.unfilledPlaceholder;

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (hasUnfilledPlaceholder) {
			return;
		}

		if (!name.trim() || !audiencePrompt.trim()) {
			toast.error('Name and prompt are required');
			return;
		}

		const request: KeepInTouchTemplateRequest = {
			name: name.trim(),
			audiencePrompt: audiencePrompt.trim(),
			objectivePrompt: objectivePrompt.trim() || undefined,
			scope,
			scheduleEnabled: enabled,
		};
		if (isEditing) {
			updateMutation.mutate({ ...editingConfig, ...request });
		} else {
			createMutation.mutate(request);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>{isEditing ? 'Edit Configuration' : 'New Configuration'}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<form id='kit-config-form' className={css(styleSheet.form)} onSubmit={handleSubmit}>
						<div className={css(styleSheet.field)}>
							<label className={css(styleSheet.label)} htmlFor='kit-config-name'>
								Name
							</label>
							<Input
								id='kit-config-name'
								value={name}
								onChange={e => setName(e.target.value)}
								placeholder='e.g., Stale VIP Clients'
								required
							/>
						</div>
						<div className={css(styleSheet.field)}>
							<label className={css(styleSheet.label)} htmlFor='kit-config-prompt'>
								Audience
							</label>
							<AIPromptField
								field={audienceField}
								footer={
									<AIPromptBoxFooter styles={[styleSheet.footer]}>
										<InputGroupText styles={[styleSheet.hint]}>
											Controls which contacts are selected for research.
										</InputGroupText>
										<InputGroupText styles={[styleSheet.hint]}>
											{audiencePrompt.length.toLocaleString()}/{MAX_AUDIENCE_LENGTH.toLocaleString()}
										</InputGroupText>
									</AIPromptBoxFooter>
								}
								id='kit-config-prompt'
								maxLength={MAX_AUDIENCE_LENGTH}
								placeholder="Describe the contacts you want to research, e.g., 'My top clients tagged VIP that I haven't spoken to in over 60 days'"
								rows={4}
								usage={AIPromptUsage.KeepInTouch}
							/>
						</div>
						<div className={css(styleSheet.field)}>
							<label className={css(styleSheet.label)} htmlFor='kit-config-research'>
								Research Instructions
							</label>
							<AIPromptField
								field={researchField}
								footer={
									<AIPromptBoxFooter>
										<InputGroupText styles={[styleSheet.hint]}>
											Optional. Guides how contacts are scored and researched — does not affect which contacts are
											selected.
										</InputGroupText>
									</AIPromptBoxFooter>
								}
								id='kit-config-research'
								placeholder='e.g., Focus on cross-sell opportunities. Prioritize contacts with only one policy line.'
								rows={3}
								usage={AIPromptUsage.KeepInTouchResearch}
							/>
						</div>
						<div className={css(styleSheet.field)}>
							<label className={css(styleSheet.label)}>Scope</label>
							<Select
								value={scope}
								onValueChange={(val: string) => setScope(val as KeepInTouchTemplateRequest['scope'])}
							>
								<SelectTrigger>
									<span>
										{scope === Api.AIAgentTemplateScope.Account ? 'Shared (all users)' : 'Private (only you)'}
									</span>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={Api.AIAgentTemplateScope.User}>Private (only you)</SelectItem>
									{isAdmin ? (
										<SelectItem value={Api.AIAgentTemplateScope.Account}>Shared (all users)</SelectItem>
									) : null}
								</SelectContent>
							</Select>
						</div>
						<div className={css(styleSheet.toggleRow)}>
							<label className={css(styleSheet.label)}>Run on nightly schedule</label>
							<Toggle isOn={enabled} onToggleCheckChanged={setEnabled} />
						</div>
					</form>
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={() => onOpenChange(false)} disabled={isPending}>
						Cancel
					</Button>
					<Button type='submit' form='kit-config-form' isLoading={isPending} disabled={hasUnfilledPlaceholder}>
						{isEditing ? 'Save Changes' : 'Create'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
